/**
 * 管理员支付订单管理相关 Store
 * 负责管理员端支付订单数据的加载、管理和操作
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminPaymentsApi } from '@/https'
import type { PaymentOrderDetail, PaymentOrderListItem } from '@/https'
import type {
    AdminPaymentOrderListRequest,
    AdminCancelPaymentRequest,
    AdminCompleteTransactionRequest,
    AdminConfirmPaymentRequest,
    SellerConfirmReturn,
    AdminOrderStatistics
} from '@/https/types_admin'
import type { AdminForceCompleteRequest } from '@/https/types/payments'

export const useAdminPaymentsStore = defineStore('admin-payments', () => {
    const cacheStore = useCacheStore()

    // ===== 状态管理 =====
    const orderList = ref<PaymentOrderListItem[]>([])
    const currentOrderDetail = ref<PaymentOrderDetail | null>(null)
    const orderStatistics = ref<AdminOrderStatistics | null>(null)

    // 分页信息
    const orderPagination = ref({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastOrderQuery = ref<AdminPaymentOrderListRequest>({})

    // 加载状态
    const orderListLoading = ref(false)
    const orderDetailLoading = ref(false)
    const orderOperationLoading = ref(false)
    const statisticsLoading = ref(false)

    // 错误状态
    const orderListError = ref<string | null>(null)
    const orderDetailError = ref<string | null>(null)
    const orderOperationError = ref<string | null>(null)
    const statisticsError = ref<string | null>(null)

    // ===== 计算属性 =====
    const ordersByStatus = computed(() => {
        const groups: Record<string, PaymentOrderListItem[]> = {}
        orderList.value.forEach(order => {
            const status = order.status || 'unknown'
            if (!groups[status]) {
                groups[status] = []
            }
            groups[status].push(order)
        })
        return groups
    })

    const hasMoreOrders = computed(() => orderPagination.value.has_more)

    // 格式化金额的计算属性
    const formattedOrderList = computed(() => {
        return orderList.value.map(order => ({
            ...order,
            total_amount_yuan: (order.total_amount / 100).toFixed(2),
            rental_amount_yuan: (order.rental_amount / 100).toFixed(2),
            deposit_amount_yuan: (order.deposit_amount / 100).toFixed(2),
            commission_amount_yuan: (order.commission_amount / 100).toFixed(2),
            consumption_amount_yuan: (order.consumption_amount / 100).toFixed(2)
        }))
    })

    // ===== 数据加载方法 =====

    /**
     * 加载支付订单列表
     */
    const loadPaymentOrderList = async (params?: AdminPaymentOrderListRequest, forceRefresh = false) => {
        const query = { ...lastOrderQuery.value, ...params }
        lastOrderQuery.value = query

        try {
            orderListLoading.value = true
            orderListError.value = null

            const response = await cacheStore.smartCache(
                `admin-payments:orders:${JSON.stringify(query)}`,
                () => adminPaymentsApi.getOrderList(query),
                forceRefresh
            )
            orderList.value = response.items
            orderPagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载支付订单列表失败'
            orderListError.value = errorMessage
            console.error('加载支付订单列表失败:', error)
        } finally {
            orderListLoading.value = false
        }
    }

    /**
     * 加载支付订单详情
     */
    const loadPaymentOrderDetail = async (id: number, forceRefresh = false) => {
        try {
            orderDetailLoading.value = true
            orderDetailError.value = null

            const response = await cacheStore.smartCache(
                `admin-payments:detail:${id}`,
                () => adminPaymentsApi.getOrderDetail(id),
                forceRefresh
            )
            currentOrderDetail.value = response
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载支付订单详情失败'
            orderDetailError.value = errorMessage
            console.error(`加载支付订单详情失败 (ID: ${id}):`, error)
        } finally {
            orderDetailLoading.value = false
        }
    }

    /**
     * 加载管理员订单统计数据
     */
    const loadOrderStatistics = async (forceRefresh = false) => {
        try {
            statisticsLoading.value = true
            statisticsError.value = null

            const response = await cacheStore.smartCache(
                'admin-payments:order-statistics',
                () => adminPaymentsApi.getOrderStatistics(),
                forceRefresh
            )
            orderStatistics.value = response
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载订单统计失败'
            statisticsError.value = errorMessage
            console.error('加载订单统计失败:', error)
        } finally {
            statisticsLoading.value = false
        }
    }

    // ===== 业务操作方法 =====

    /**
     * 管理员取消支付
     */
    const cancelPayment = async (id: number, data?: AdminCancelPaymentRequest) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.cancelPayment(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
            await loadPaymentOrderDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '取消支付失败'
            orderOperationError.value = errorMessage
            console.error(`取消支付失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 管理员最终确认交易完成
     */
    const completeTransaction = async (id: number, data?: AdminCompleteTransactionRequest) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.completeTransaction(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
            await loadPaymentOrderDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '确认交易完成失败'
            orderOperationError.value = errorMessage
            console.error(`确认交易完成失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 管理员确认支付
     */
    const confirmPayment = async (id: number, data?: AdminConfirmPaymentRequest) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.confirmPayment(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
            await loadPaymentOrderDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '确认支付失败'
            orderOperationError.value = errorMessage
            console.error(`确认支付失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 管理员强制买家确认完成
     */
    const forceBuyerComplete = async (id: number) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.forceBuyerComplete(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
            await loadPaymentOrderDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '强制买家完成失败'
            orderOperationError.value = errorMessage
            console.error(`强制买家完成失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 管理员强制卖家确认归还
     */
    const forceSellerConfirm = async (id: number, data: SellerConfirmReturn) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.forceSellerConfirm(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
            await loadPaymentOrderDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '强制卖家确认失败'
            orderOperationError.value = errorMessage
            console.error(`强制卖家确认失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 恢复已删除的订单
     */
    const restoreOrder = async (id: number) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.restoreOrder(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
            await loadPaymentOrderDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '恢复订单失败'
            orderOperationError.value = errorMessage
            console.error(`恢复订单失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 管理员撤回（将 SELLER_CONFIRMED 回退为 BUYER_COMPLETED，并清空消耗数据）
     */
    const revokeOrder = async (id: number) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.revokeOrder(id)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
            await loadPaymentOrderDetail(id, true) // 重新加载详情
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '撤回操作失败'
            orderOperationError.value = errorMessage
            console.error(`撤回操作失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 管理员强制完成订单（一步到位完成整个交易流程）
     */
    const forceComplete = async (id: number, data: AdminForceCompleteRequest) => {
        try {
            orderOperationLoading.value = true
            orderOperationError.value = null
            await adminPaymentsApi.forceComplete(id, data)
            // 清除相关缓存
            cacheStore.removeCacheByPrefix('admin-payments:orders')
            cacheStore.removeCacheByPrefix(`admin-payments:detail:${id}`)
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '强制完成订单失败'
            orderOperationError.value = errorMessage
            console.error(`强制完成订单失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    // ===== 工具方法 =====

    /**
     * 根据ID查找订单
     */
    const findOrderById = (id: number) => {
        return orderList.value.find(order => order.id === id)
    }

    /**
     * 清除所有状态
     */
    const clearAllStates = () => {
        orderList.value = []
        currentOrderDetail.value = null
        orderStatistics.value = null
        orderPagination.value = { total: 0, page: 1, size: 20, has_more: false }
        lastOrderQuery.value = {}
        // 清除错误状态
        orderListError.value = null
        orderDetailError.value = null
        orderOperationError.value = null
        statisticsError.value = null
    }

    return {
        // 状态
        orderList,
        currentOrderDetail,
        orderStatistics,
        orderPagination,
        lastOrderQuery,

        // 加载状态
        orderListLoading,
        orderDetailLoading,
        orderOperationLoading,
        statisticsLoading,

        // 错误状态
        orderListError,
        orderDetailError,
        orderOperationError,
        statisticsError,

        // 计算属性
        ordersByStatus,
        hasMoreOrders,
        formattedOrderList,

        // 数据加载方法
        loadPaymentOrderList,
        loadPaymentOrderDetail,
        loadOrderStatistics,

        // 业务操作方法
        cancelPayment,
        completeTransaction,
        confirmPayment,
        forceBuyerComplete,
        forceSellerConfirm,
        restoreOrder,
        revokeOrder,
        forceComplete,

        // 工具方法
        findOrderById,
        clearAllStates
    }
}) 